import { appendDispatchDateToUrl } from "./dispatchDateUrls.js";

export const IMPORT_INVALIDATED_API_URLS = ["/api/dashboard", "/api/dashboard/home", "/api/data-manager/state", "/api/results/latest"];
export const SHOW_FORCE_REPLACE_ACTION = true;

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeSubmissionStatus(status) {
  return typeof status === "string" && status.trim()
    ? status.trim()
    : "idle";
}

function normalizeSubmissionFeedback(feedback) {
  return isPlainObject(feedback) ? feedback : null;
}

function normalizeSubmissionResults(results) {
  return Array.isArray(results) ? [...results] : [];
}

export function createInitialImportSubmissionState() {
  return {
    completionNotice: null,
    feedback: null,
    impact: null,
    issue: null,
    results: [],
    shouldInvalidateCache: false,
    shouldResetSelection: false,
    status: "idle",
    submissionMode: "selection",
  };
}

export function buildPendingImportSubmissionState() {
  return createInitialImportSubmissionState();
}

export function buildImportSubmissionState({
  completionNotice = null,
  feedback = null,
  impact = null,
  results = [],
  shouldInvalidateCache = false,
  shouldResetSelection = false,
  status = "idle",
  submissionMode = "selection",
} = {}) {
  const normalizedFeedback = normalizeSubmissionFeedback(feedback);

  return {
    completionNotice: isPlainObject(completionNotice) ? completionNotice : null,
    feedback: normalizedFeedback,
    impact: isPlainObject(impact) ? impact : null,
    issue: isPlainObject(normalizedFeedback?.issue) ? normalizedFeedback.issue : null,
    results: normalizeSubmissionResults(results),
    shouldInvalidateCache: Boolean(shouldInvalidateCache),
    shouldResetSelection: Boolean(shouldResetSelection),
    status: normalizeSubmissionStatus(status),
    // Mode de l'envoi qui a produit cet état : « Confirmer l’import » le reprend.
    submissionMode: submissionMode === "force" ? "force" : "selection",
  };
}

export function createSollioConversionErrorSubmissionState({ fileName = "", message = "" } = {}) {
  const resolvedMessage = typeof message === "string" && message.trim()
    ? message.trim()
    : "La conversion Sollio a échoué.";

  return buildImportSubmissionState({
    feedback: {
      issue: {
        code: "sollio.conversion_error",
      },
      message: resolvedMessage,
      tone: "danger",
    },
    results: [{
      field: "sollio",
      filename: typeof fileName === "string" ? fileName : "",
      message: resolvedMessage,
      status: "error",
    }],
    status: "backend-error",
  });
}

export function createUniagConversionErrorSubmissionState({ fileName = "", message = "" } = {}) {
  const resolvedMessage = typeof message === "string" && message.trim()
    ? message.trim()
    : "La conversion Uniag a échoué.";

  return buildImportSubmissionState({
    feedback: {
      issue: {
        code: "uniag.conversion_error",
      },
      message: resolvedMessage,
      tone: "danger",
    },
    results: [{
      field: "uniag",
      filename: typeof fileName === "string" ? fileName : "",
      message: resolvedMessage,
      status: "error",
    }],
    status: "backend-error",
  });
}

function normalizeSummaryText(summary) {
  return typeof summary === "string" ? summary.trim() : "";
}

function hasSnapshotStaleFollowUp(impact) {
  if (!isPlainObject(impact) || impact.has_real_changes === false) {
    return false;
  }

  const signals = isPlainObject(impact.signals) ? impact.signals : {};
  return Boolean(signals.snapshot_active);
}

function hasSuccessfulImportResult(results) {
  return Array.isArray(results) && results.some((result) => result?.status === "success");
}

function hasOnlyUnchangedImportResults(results) {
  return Array.isArray(results)
    && results.length > 0
    && results.every((result) => result?.status === "unchanged");
}

function buildCompletedImportNoticeTitle(submissionMode) {
  return submissionMode === "force"
    ? "Remplacement complet appliqué"
    : "Import terminé";
}

function buildCompletedImportNoticeMessage({
  hasSnapshotFollowUp,
  submissionMode,
  summary,
}) {
  if (hasSnapshotFollowUp) {
    if (submissionMode === "force") {
      return "Sources remplacées, planning effacé. Ouvrez le Dispatch, puis « Optimiser la flotte ».";
    }

    return summary
      ? `${summary} Ouvrez le Dispatch, puis « Mise à jour ».`
      : "Sources mises à jour. Ouvrez le Dispatch, puis « Mise à jour ».";
  }

  if (submissionMode === "force") {
    return "Le remplacement des sources sélectionnées est terminé. Le planning courant a été réinitialisé.";
  }

  return summary
    ? `La mise à jour des sources sélectionnées est terminée. ${summary}`
    : "La mise à jour des sources sélectionnées est terminée.";
}

export function buildImportLoadingToastContent({ submissionMode = "selection" } = {}) {
  const isForceReplace = submissionMode === "force";

  if (isForceReplace) {
    return {
      errorTitle: "Remplacement interrompu",
      message: "Nous remplaçons les sources sélectionnées, effaçons le planning lié et rechargeons les vues dépendantes.",
      title: "Remplacement en cours",
    };
  }

  return {
    errorTitle: "Import interrompu",
    message: "Nous analysons les fichiers sélectionnés, mettons à jour les sources et rechargeons les vues dépendantes.",
    title: "Import en cours",
  };
}

export function buildImportRunningShellNotice({ submissionMode = "selection" } = {}) {
  const isForceReplace = submissionMode === "force";
  const loadingContent = buildImportLoadingToastContent({ submissionMode });

  return {
    eyebrow: isForceReplace ? "Remplacement complet" : "Import Excel",
    message: loadingContent.message,
    source: "import",
    startedAt: new Date().toISOString(),
    status: "running",
    statusLabel: "Traitement en cours",
    title: loadingContent.title,
  };
}

export function buildImportFailedShellNotice({
  message = "L'import a échoué. Vérifiez le détail ci-dessous.",
  submissionMode = "selection",
} = {}) {
  const loadingContent = buildImportLoadingToastContent({ submissionMode });
  const isForceReplace = submissionMode === "force";

  return {
    eyebrow: isForceReplace ? "Remplacement complet" : "Import Excel",
    finishedAt: new Date().toISOString(),
    message,
    source: "import",
    status: "failed",
    statusLabel: "Échec",
    title: loadingContent.errorTitle,
  };
}

export function buildImportFormDataEntries({
  bypassOrderImportGuards = false,
  fieldConfigs,
  filesByField,
  confirmBreakingImport = false,
  submissionMode = "selection",
}) {
  const normalizedFieldConfigs = Array.isArray(fieldConfigs) ? fieldConfigs : [];
  const normalizedFilesByField = isPlainObject(filesByField) ? filesByField : {};
  const entries = [];

  normalizedFieldConfigs.forEach(({ field }) => {
    if (typeof field !== "string" || !field) {
      return;
    }

    const file = normalizedFilesByField[field];
    if (file) {
      entries.push([field, file]);
    }
  });

  if (confirmBreakingImport) {
    entries.push(["confirm_breaking_import", "true"]);
  }

  if (bypassOrderImportGuards) {
    entries.push(["bypass_order_import_guards", "true"]);
  }

  entries.push(["submission_mode", submissionMode === "force" ? "force" : "selection"]);

  return entries;
}

export function buildImportFormData(options) {
  const formData = new FormData();

  buildImportFormDataEntries(options).forEach(([field, value]) => {
    formData.append(field, value);
  });

  return formData;
}

export function shouldInvalidateImportCache(results) {
  return Array.isArray(results) && results.some((result) => result?.status === "success");
}

export function shouldPersistImportImpactOnSuccess(impact) {
  return isPlainObject(impact) && impact.level === "minor" && impact.has_real_changes !== false;
}

export function resolveVisibleImportImpact({ responseOk, impact }) {
  if (!isPlainObject(impact)) {
    return null;
  }

  if (!responseOk) {
    return impact;
  }

  return shouldPersistImportImpactOnSuccess(impact) ? impact : null;
}

export function buildImportCompletionShellNotice({
  impact,
  responseOk = false,
  results,
  resolveResultLabel = (field) => field || "Requête",
  submissionMode = "selection",
}) {
  if (hasOnlyUnchangedImportResults(results)) {
    return null;
  }

  const hasSuccessfulResult = responseOk || hasSuccessfulImportResult(results);
  if (!hasSuccessfulResult) {
    return null;
  }

  const hasSnapshotFollowUp = hasSnapshotStaleFollowUp(impact);
  const replacedLabels = Array.isArray(results)
    ? results
      .filter((result) => result?.status === "success")
      .map((result) => resolveResultLabel(result?.field))
      .filter(Boolean)
    : [];
  const summary = normalizeSummaryText(impact?.summary);
  const resultUrl = appendDispatchDateToUrl("/results");

  return {
    detailItems: [
      ...(hasSnapshotFollowUp ? [{
        key: "next-step",
        label: "Étape suivante",
        value: submissionMode === "force" ? "Optimiser la flotte" : "Mise à jour",
      }] : []),
      ...(replacedLabels.length > 0 ? [{
        key: "replaced-sources",
        label: "Sources remplacées",
        value: replacedLabels.join(", "),
      }] : []),
    ],
    eyebrow: "Import Excel",
    finishedAt: new Date().toISOString(),
    message: buildCompletedImportNoticeMessage({
      hasSnapshotFollowUp,
      submissionMode,
      summary,
    }),
    resultActionLabel: "Ouvrir le Dispatch",
    resultUrl,
    source: "import",
    status: "completed",
    statusLabel: hasSnapshotFollowUp ? "Action requise" : "Import terminé",
    title: buildCompletedImportNoticeTitle(submissionMode),
  };
}
